import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IRole } from '@core/interfaces/role.interface';
import { PermissionService } from '@core/services/permission.service';
import { RoleService } from '@core/services/role.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { getRoleIcon } from '@utilities/role-icon.util';
import { translateRoleName } from '@utilities/role-name.util';
import { roleExceedsPermissions } from '@utilities/role-permission-comparison.util';
import {
  buildRolePermissions,
  deriveEnabledToggleKeys,
  IRolePermissionSection,
  IRolePermissionToggle,
  ROLE_PERMISSION_SECTIONS,
  toggleKeyFor,
} from '@utilities/role-permission-groups.util';
import { filter } from 'rxjs';
import { IAddCustomRoleDialogData } from './add-custom-role-dialog.interface';

/** Fallback icon for every custom role, matching `getRoleIcon()`'s own convention - used here only
 * in 'create' mode, since there's no role yet to derive an icon from. */
const CUSTOM_ROLE_ICON = '/icons/user-cog.svg';

/**
 * Single dialog covering all 3 "role" screens sharing the same permissions-form UI, driven by
 * `data.mode`:
 * - 'create' - blank form (Select Role's "Add custom role" flow, and the Roles & Teams tab's "Add
 *   Custom Role" button). Save calls `RoleService.createRole`.
 * - 'edit' - pre-filled from `data.role` (always a custom role). Save calls
 *   `RoleService.updateRole`. Only reachable from the Roles & Teams tab.
 * - 'view' - pre-filled from `data.role` (always a system role, Admin/Member - those can't be
 *   edited at all), every control disabled, single "Close" action.
 *
 * Uses the same `app-side-dialog` shell as Select Role/Select Team/Add Team, with the same
 * discard-confirmation-on-close pattern (skipped entirely in 'view' mode, since nothing there can
 * change).
 */
@Component({
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslateModule,
    MatErrorComponent,
    DotsSpinnerComponent,
    IconComponent,
    IconBadgeComponent,
    SideDialogComponent,
  ],
  selector: 'app-add-custom-role-dialog',
  templateUrl: './add-custom-role-dialog.component.html',
})
export class AddCustomRoleDialogComponent {
  public readonly data = inject<IAddCustomRoleDialogData>(MAT_DIALOG_DATA);

  // Declared early (ahead of `defaultCreateName`/`initialEnabledToggleKeys` below) - TS class
  // fields initialize in declaration order, and those need `this.translate`/
  // `this.currentUserPermissions` already set.
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);

  /** The currently logged-in user's own permissions - not the role being created/edited/viewed.
   * Reactive (rather than a one-off snapshot) so a permission change elsewhere in the session
   * (e.g. this user's own role being edited by someone else) is reflected immediately. */
  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  public readonly isCreateMode = this.data.mode === 'create';
  public readonly isEditMode = this.data.mode === 'edit';
  public readonly isViewMode = this.data.mode === 'view';

  /** 'view' mode alone doesn't mean this is a genuine system role - see IAddCustomRoleDialogData's
   * `isProtected` doc comment. Only true for Admin/Member; drives the "System role" badge/caption
   * in the template. */
  public readonly isProtected = !!this.data.isProtected;

  /** True for a (non-protected) custom role that grants at least one permission the current user
   * doesn't hold themselves - the reason UserManagementRolesTeamsTabComponent's `canManageRole`
   * forced this dialog open in 'view' mode in the first place. Drives the warning lock icon/
   * caption in the template, distinct from the `isProtected` system-role badge above. */
  public readonly exceedsCurrentUserPermissions = computed(
    () => !!this.data.role && roleExceedsPermissions(this.data.role, this.currentUserPermissions()),
  );

  public readonly sections: IRolePermissionSection[] = ROLE_PERMISSION_SECTIONS;

  public readonly identityIcon = this.data.role ? getRoleIcon(this.data.role) : CUSTOM_ROLE_ICON;

  private readonly defaultCreateName = this.translate.instant(
    'pages.settings.userManagement.addCustomRoleDialog.defaultName',
    { number: (this.data.roles ?? []).filter((role) => !!role.userGroupId).length + 1 },
  ) as string;

  public readonly form = new FormGroup({
    name: new FormControl<string | null>(this.data.role?.name ?? this.defaultCreateName, Validators.required),
  });

  /** 'create' defaults ON every toggle the current user can actually manage (least-surprising
   * default for a brand-new role - the caller almost always wants the role to start out
   * mirroring their own access) and leaves every other toggle OFF - a user must never be able to
   * create a role broader than their own, so a toggle they don't hold themselves is both disabled
   * (see isToggleDisabled) and excluded from this default, not merely disabled-while-checked.
   * 'edit'/'view' derive the actual saved state from `role.permissions` instead - an existing
   * role's already-granted permissions are preserved even if the current user can no longer
   * manage them (see canManageToggle's own doc comment). */
  private readonly initialEnabledToggleKeys = this.data.role
    ? deriveEnabledToggleKeys(this.data.role.permissions)
    : new Set<string>(
        this.sections.flatMap((section) =>
          section.toggles
            .filter((toggle) => this.canManageToggle(toggle))
            .map((toggle) => toggleKeyFor(section.key, toggle.key)),
        ),
      );

  public readonly enabledToggleKeys = signal<Set<string>>(new Set(this.initialEnabledToggleKeys));

  public readonly expandedSectionKeys = signal<Set<string>>(new Set(this.sections.map((section) => section.key)));
  public readonly allSectionsExpanded = computed(() => this.expandedSectionKeys().size === this.sections.length);

  public readonly submitLoading = signal<boolean>(false);

  /** Set the moment any permission toggle is flipped, and never cleared - so toggling a
   * permission on and back off still counts as a change, same as `form.dirty` below never
   * resetting once the name field has been touched. A plain value comparison against the initial
   * state would miss that "toggled then reverted" case entirely. */
  private readonly permissionsChanged = signal<boolean>(false);

  /** Every toggle key the user has actually flipped this session (via onToggleChange), plus any
   * key createUnmanageableTogglePruneEffect has forced off - passed to buildRolePermissions so it
   * can tell a deliberate on/off decision apart from a toggle that merely *looks* off because the
   * existing role's permissions only partially matched its bundle (see that function's own doc
   * comment). Never cleared, same reasoning as permissionsChanged above. */
  private readonly touchedToggleKeys = signal<Set<string>>(new Set<string>());

  /** True once the name or any permission toggle has ever been changed - even if it ended up back
   * at its original value. `form.dirty` already has this "ever touched, never un-dirty" semantics
   * natively; `permissionsChanged` mirrors it by hand since the toggles aren't form controls. */
  public readonly hasChanged = computed(() => this.form.dirty || this.permissionsChanged());

  private readonly dialogRef = inject(MatDialogRef<AddCustomRoleDialogComponent, IRole | undefined>);
  private readonly dialog = inject(MatDialog);
  private readonly roleService = inject(RoleService);

  /** Shown as a tooltip on any toggle/label the current user can't manage - see canManageToggle. */
  public readonly cannotManagePermissionMessage = this.translate.instant(
    'pages.settings.userManagement.addCustomRoleDialog.cannotManagePermission',
  ) as string;

  constructor() {
    if (this.isViewMode) {
      this.form.disable();
    }

    // 'view' mode's toggles reflect the role's actually-saved permissions, not the viewer's own -
    // pruning there would incorrectly alter that read-only display (exceedsCurrentUserPermissions
    // already surfaces the "you don't hold all of this" warning without touching toggle state).
    if (!this.isViewMode) {
      this.createUnmanageableTogglePruneEffect();
    }
  }

  public getIdentityName(): string {
    return this.data.role ? translateRoleName(this.data.role, this.translate) : '';
  }

  public getSectionEnabledCount(section: IRolePermissionSection): number {
    return section.toggles.filter((toggle) => this.isToggleEnabled(section.key, toggle.key)).length;
  }

  public isToggleEnabled(sectionKey: string, toggleKey: string): boolean {
    return this.enabledToggleKeys().has(toggleKeyFor(sectionKey, toggleKey));
  }

  /** A toggle can only be granted/revoked by someone who already holds every permission it
   * bundles - otherwise saving this role could hand out (or take away) access the current user
   * doesn't have themselves. True only when the current user holds every one of the toggle's
   * underlying permissions. */
  public canManageToggle(toggle: IRolePermissionToggle): boolean {
    const userPermissions = this.currentUserPermissions();

    return toggle.permissions.every((permission) => userPermissions.includes(permission));
  }

  /** 'view' mode disables every toggle for its own (read-only) reason - this only adds the
   * permission-based disabling on top for 'create'/'edit', so the two reasons aren't conflated in
   * the tooltip (see cannotManagePermissionMessage, only shown for the latter). */
  public isToggleDisabled(toggle: IRolePermissionToggle): boolean {
    return this.isViewMode || !this.canManageToggle(toggle);
  }

  /** If the current user's own permissions shrink while this dialog is open (e.g. someone else
   * edits their role concurrently), any toggle that was checked a moment ago but is no longer
   * manageable (canManageToggle) must be unchecked too - otherwise `isToggleDisabled` greys it out
   * in the UI while `enabledToggleKeys` silently keeps carrying it, and Save would submit a role
   * that grants a permission the current user doesn't hold themselves any more, exactly what
   * canManageToggle/isToggleDisabled exist to prevent. Only ever prunes - regaining a permission
   * never re-adds a toggle, since that's the user regaining the *ability* to grant/revoke that
   * bit, not a reason to flip it back on for them.
   *
   * Only a key actually removed *from* `enabledToggleKeys` here is marked "touched" - not merely
   * every currently-unmanageable one. A toggle this editor could never manage in the first place
   * (e.g. an existing role's partially-granted bundle the editor doesn't fully hold) is
   * unmanageable from the moment this effect first runs, without ever having been in
   * `enabledToggleKeys` to begin with - marking it touched anyway would make buildRolePermissions
   * drop that partial bundle on any unrelated edit (a plain rename included), which is the bug
   * this distinction exists to avoid. A toggle that genuinely was enabled and just lost
   * manageability, though, still needs marking touched - that's a real, live downgrade, and
   * leaving it "untouched" would let buildRolePermissions hand the full bundle right back from the
   * role's existing permissions, defeating the whole point of this effect. */
  private createUnmanageableTogglePruneEffect(): void {
    effect(() => {
      this.currentUserPermissions();

      const unmanageableKeys = this.sections.flatMap((section) =>
        section.toggles
          .filter((toggle) => !this.canManageToggle(toggle))
          .map((toggle) => toggleKeyFor(section.key, toggle.key)),
      );

      if (!unmanageableKeys.length) {
        return;
      }

      const prunedKeys: string[] = [];

      this.enabledToggleKeys.update((current) => {
        let changed = false;
        const next = new Set(current);

        for (const key of unmanageableKeys) {
          if (next.delete(key)) {
            changed = true;
            prunedKeys.push(key);
          }
        }

        return changed ? next : current;
      });

      if (!prunedKeys.length) {
        return;
      }

      this.touchedToggleKeys.update((current) => {
        const next = new Set(current);

        for (const key of prunedKeys) {
          next.add(key);
        }

        return next;
      });
    });
  }

  public onToggleChange(sectionKey: string, toggleKey: string, checked: boolean): void {
    const key = toggleKeyFor(sectionKey, toggleKey);

    this.permissionsChanged.set(true);
    this.touchedToggleKeys.update((current) => (current.has(key) ? current : new Set(current).add(key)));

    this.enabledToggleKeys.update((current) => {
      const next = new Set(current);

      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }

      return next;
    });
  }

  public isSectionExpanded(sectionKey: string): boolean {
    return this.expandedSectionKeys().has(sectionKey);
  }

  public onSectionToggle(sectionKey: string, expanded: boolean): void {
    this.expandedSectionKeys.update((current) => {
      const next = new Set(current);

      if (expanded) {
        next.add(sectionKey);
      } else {
        next.delete(sectionKey);
      }

      return next;
    });
  }

  public onExpandCollapseAllClick(): void {
    this.expandedSectionKeys.set(
      this.allSectionsExpanded() ? new Set<string>() : new Set(this.sections.map((section) => section.key)),
    );
  }

  public onSaveClick(): void {
    if (this.form.invalid || this.submitLoading()) {
      this.form.markAllAsTouched();

      return;
    }

    const name = this.form.value.name!;
    const permissions = buildRolePermissions(
      this.enabledToggleKeys(),
      this.touchedToggleKeys(),
      this.data.role?.permissions ?? [],
    );

    this.submitLoading.set(true);

    const request$ =
      this.isEditMode && this.data.role
        ? this.roleService.updateRole(this.data.role.id, { name, permissions })
        : this.roleService.createRole({ name, permissions });

    // No explicit error snackbar here - the global ApiInterceptor already shows one for the
    // failed request (with the actual backend message, where there is one), and a second,
    // generic snackbar here would just immediately replace it with a less useful message
    // (SnackbarService only ever shows one snackbar at a time).
    request$.subscribe({
      error: () => {
        this.submitLoading.set(false);
      },
      next: (role) => {
        this.submitLoading.set(false);
        this.dialogRef.close(role);
      },
    });
  }

  public onCloseClick(): void {
    if (this.submitLoading()) {
      return;
    }

    if (this.isViewMode || !this.hasChanged()) {
      this.dialogRef.close(undefined);

      return;
    }

    const confirmRef = this.dialog.open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.cancel'),
        confirmText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.confirm'),
        message: this.translate.instant('pages.settings.userManagement.discardChangesDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.settings.userManagement.discardChangesDialog.title'),
      } as IConfirmDialogData,
      width: '400px',
    });

    confirmRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => this.dialogRef.close(undefined));
  }
}
